type ToneOptions = {
    duration?: number
    gain?: number
    type?: OscillatorType
    delay?: number
}

type AudioWindow = Window & {
    webkitAudioContext?: typeof AudioContext
}

const PENTATONIC = [0, 2, 4, 7, 9]

let context: AudioContext | null = null
let voices = 0
let lastTick = 0

/** Creates or resumes the audio context. Call it from a click so the browser allows sound. */
export const unlockAudio = (): boolean => {
    if (typeof window === 'undefined') return false

    if (!context) {
        const audioWindow = window as AudioWindow
        const Context = window.AudioContext || audioWindow.webkitAudioContext
        if (!Context) return false
        context = new Context()
    }

    if (context.state === 'suspended') void context.resume()

    return true
}

export const noteFrequency = (step: number, base = 392): number => {
    const octave = Math.floor(step / PENTATONIC.length)
    const degree = ((step % PENTATONIC.length) + PENTATONIC.length) % PENTATONIC.length
    return base * Math.pow(2, (PENTATONIC[degree] + 12 * octave) / 12)
}

export const playTone = (frequency: number, { duration = 0.08, gain = 0.03, type = 'sine', delay = 0 }: ToneOptions = {}): void => {
    if (!context || context.state !== 'running' || voices > 14) return

    const start = context.currentTime + delay
    const oscillator = context.createOscillator()
    const amplifier = context.createGain()

    oscillator.type = type
    oscillator.frequency.setValueAtTime(frequency, start)
    amplifier.gain.setValueAtTime(0.0001, start)
    amplifier.gain.exponentialRampToValueAtTime(gain, start + 0.006)
    amplifier.gain.exponentialRampToValueAtTime(0.0001, start + duration)
    oscillator.connect(amplifier)
    amplifier.connect(context.destination)

    voices += 1
    oscillator.onended = () => {
        voices -= 1
        oscillator.disconnect()
        amplifier.disconnect()
    }

    oscillator.start(start)
    oscillator.stop(start + duration + 0.03)
}

/** Short peg tick, rate limited so a busy board never turns into noise. */
export const playTick = (step: number): void => {
    const now = typeof performance === 'undefined' ? 0 : performance.now()
    if (now - lastTick < 22) return
    lastTick = now
    playTone(noteFrequency(step), { duration: 0.07, gain: 0.018, type: 'triangle' })
}

/** Landing chime: a single soft note for small multipliers, a rising arpeggio for big ones. */
export const playChime = (intensity: number): void => {
    if (intensity < 0.35) {
        playTone(noteFrequency(1, 196), { duration: 0.22, gain: 0.03 })
        return
    }

    const root = Math.round(intensity * 6)
    const steps = intensity > 0.75 ? [root, root + 2, root + 4] : [root, root + 2]

    steps.forEach((step, index) => {
        playTone(noteFrequency(step, 330), { duration: 0.32, gain: 0.034, type: 'sine', delay: index * 0.07 })
    })
}
